import React, {useEffect, useMemo, useState} from 'react';
import {getPublishedDirectory, publishedDirectoryCsv} from './publishedDirectory';
import './SLTDirectoryExport.css';

export default function SLTDirectoryExport({lots, search = '', onBack}) {
    const [scope, setScope] = useState('all');
    const [includeMailingAddresses, setIncludeMailingAddresses] = useState(false);
    const [error, setError] = useState(null);
    const [generated] = useState(() => new Date());
    useEffect(() => {
        const title = document.title;
        document.title = 'Golden Bear Reserve Directory';
        return () => { document.title = title; };
    }, []);
    const households = useMemo(() => getPublishedDirectory(lots, {
        includeMailingAddresses, search: scope === 'search' ? search : '',
    }), [lots, includeMailingAddresses, scope, search]);
    const residents = households.reduce((count, lot) => count + lot.owners.length, 0);

    const download = () => {
        let url;
        let link;
        try {
            const csv = publishedDirectoryCsv(households, {includeMailingAddresses});
            url = URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8;'}));
            link = document.createElement('a');
            link.href = url;
            const date = [generated.getFullYear(), String(generated.getMonth() + 1).padStart(2, '0'),
                String(generated.getDate()).padStart(2, '0')].join('-');
            link.download = `golden-bear-directory-${date}${scope === 'search' ? '-search-results' : ''}.csv`;
            document.body.appendChild(link);
            link.click();
            // Give Safari time to begin the download before releasing the blob URL.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            setError(null);
        } catch (failure) {
            if (url) URL.revokeObjectURL(url);
            setError('The CSV could not be downloaded. Please try again.');
        } finally {
            if (link) link.remove();
        }
    };

    return <section className="directory-export" aria-label="Print or export directory">
        <header className="directory-export-controls">
            <h1>Print / Export Directory</h1>
            <p>Print this preview or choose Save as PDF in your print dialog. Download CSV to open the directory in a spreadsheet.</p>
            <div className="directory-export-options">
                <label>Directory to include
                    <select value={scope} onChange={event => setScope(event.target.value)}>
                        <option value="all">Full published directory</option>
                        {search.trim() && <option value="search">Current search results: {search}</option>}
                    </select>
                </label>
                <label className="directory-export-checkbox">
                    <input type="checkbox" checked={includeMailingAddresses}
                        onChange={event => setIncludeMailingAddresses(event.target.checked)} />
                    Include published mailing addresses
                </label>
            </div>
            <p className="directory-export-note">Only published residents and contact details are included.</p>
            {error && <p role="alert">{error}</p>}
        </header>
        <div className="directory-export-preview" tabIndex={0} aria-label="Directory preview">
            <div className="directory-export-paper">
                <header className="directory-print-heading">
                    <h2>Golden Bear Reserve Directory</h2>
                    <p>Published neighborhood directory</p>
                    <p>{households.length} {households.length === 1 ? 'household' : 'households'} · {residents} {residents === 1 ? 'resident' : 'residents'}</p>
                    <p>Generated {generated.toLocaleDateString(undefined, {dateStyle: 'long'})}</p>
                    {scope === 'search' && <p>Search results for “{search}”</p>}
                </header>
                {!households.length && <p role="status">No published entries match these options.</p>}
                {households.map((lot, index) => <article className="directory-print-household" key={index}>
                    <header>
                        <h3>Lot {lot.lotNumber}{lot.neighborhood ? ` · ${lot.neighborhood}` : ''}</h3>
                        <p className="directory-print-address">{lot.address}</p>
                    </header>
                    {lot.owners.map((owner, ownerIndex) => <div className="directory-print-resident" key={ownerIndex}>
                        <h4>{owner.name}</h4>
                        <div>
                            {owner.phones.map((phone, i) => <p key={`phone-${i}`}>{phone}</p>)}
                            {owner.emails.map((email, i) => <p key={`email-${i}`}>{email}</p>)}
                            {owner.mailingAddresses.map((address, i) => <p className="directory-print-address" key={`mail-${i}`}>
                                <span className="directory-print-mail-label">Mailing address:</span>{address}
                            </p>)}
                        </div>
                    </div>)}
                </article>)}
            </div>
        </div>
        <footer className="directory-export-actions">
            <button type="button" disabled={!households.length} onClick={() => window.print()}>Print / Save as PDF</button>
            <button type="button" disabled={!households.length} onClick={download}>Download CSV</button>
            <button type="button" onClick={onBack}>Back to Directory</button>
        </footer>
    </section>;
}
